import { convertBooleanToYesNo } from '@fruitful/fruitils';
import {
    BankingAdoptionTaskStatus,
    GuidanceSessionType,
    MoneyMapStatus,
    YesNo,
} from '@fruitful/types';

import {
    formatPhoneNumber,
    formatToSimpleDateString,
} from '../../../common/utils/formatting';
import { JOINED_DATA_SEPARATOR } from '../../constants';
import {
    type Member,
    type MemberDetails,
    type MemberJourneyLog,
    type MembershipDetails,
    type MembershipSummary,
    MembershipType,
    UserType,
} from '../../entityTypes';
import type { MembershipDetailsDto } from '../MembershipDetailsEntityProvider';
import type {
    MembershipSummaryDto,
    MembershipSummaryGuidanceSession,
} from '../MembershipEntityProvider';

export const transposeMembershipSummaryDto = (
    membership: MembershipSummaryDto,
): MembershipSummary => {
    const members = membership.users;

    return {
        ...membership,
        membershipType: getMembershipTypeByMemberCount(members), // NOTE: This isn't always the true membershipType, but is a decent proxy for inferring the membershipType for a MembershipSummary.
        isMembershipActive: getIsMembershipActive(members),
        membersName: flattenMemberField(members, getFullName),
        membersEmail: flattenMemberField(members, (m) => m.email),
        membersPhone: flattenMemberField(members, (m) =>
            formatPhoneNumber(m.phoneNumber),
        ),
        membersId: flattenMemberField(members, (m) => m.id),
        membersState: flattenMemberField(members, (m) =>
            (m?.userAddress?.state ?? '').trim(),
        ),
        nextSession: flattenSession(getNextSession(membership.guidanceSession)),
        previousSession: flattenSession(
            getPreviousSession(membership.guidanceSession),
        ),
        hasIM:
            convertBooleanToYesNo(
                members.some(
                    (member) =>
                        member.orionUser?.id != null ||
                        (member.apexAccountParties?.length ?? 0) > 0,
                ),
            ) ?? YesNo.NO, // If either member has an orion user record, mark membership as having IM.
        memberJourneyStatus: getMemberJourneyStatus(membership),
    };
};

export const transposeMembershipDetailsDto = (
    membership: MembershipDetailsDto,
): MembershipDetails => {
    const { users: members } = membership;

    return {
        ...membership,
        membershipType: getMembershipTypeByMemberCount(members),
        isMembershipActive: getIsMembershipActive(members),
        membersName: flattenMemberField(members, getFullName),
        membersEmail: flattenMemberField(members, (m) => m.email),
        membersPhone: flattenMemberField(members, (m) =>
            formatPhoneNumber(m.phoneNumber),
        ),
        membersId: flattenMemberField(members, (m) => m.id),
        membersState: flattenMemberField(members, (m) =>
            (m?.userAddress?.state ?? '').trim(),
        ),
        users: members.map(
            (member: Member): MemberDetails =>
                transposeMemberDetails(member, members.length),
        ),
    };
};

type MemberUserFromApi = Member & {
    /** Prisma-style relation name on some admin payloads */
    MemberJourneyLog?: MemberJourneyLog | null;
};

export const transposeMemberDetails = (
    member: Member,
    memberCount: number,
): MemberDetails => {
    const { MemberJourneyLog: memberJourneyLogPascal, ...memberRest } =
        member as MemberUserFromApi;

    return {
        ...memberRest,
        memberJourneyLog:
            memberRest.memberJourneyLog ?? memberJourneyLogPascal ?? null,
        type: getMemberType(member, memberCount),
    };
};

// This method is different than the backend-app implementation since all users in Grove-app will have a membership. It currently omits JOINT_ACCOUNT_HOLDER and PROSPECT as values, since those user/member states aren't viewable in Grove.
// TODO - Only do this transposition on the backend - need to update Admin Membership/User GET requests.
const getMemberType = (member: Member, memberCount: number): UserType => {
    if (member.stripeCustomerId != null || memberCount === 1) {
        return UserType.PRIMARY_MEMBER;
    } else {
        return UserType.PARTNER_MEMBER;
    }
};

// This method is helpful to infer the membership type when using a membership type with data supplied only from our database, and not from Stripe, where the source of truth for a membership type lives.
const getMembershipTypeByMemberCount = <T>(members: T[]) => {
    return members.length < 2 ? MembershipType.SOLO : MembershipType.JOINT;
};

const getIsMembershipActive = <
    T extends { isSubscriptionActive: boolean | null },
>(
    members: T[],
) => {
    return members.filter((m) => m.isSubscriptionActive).length > 0;
};

const flattenMemberField = <T>(
    members: T[],
    fieldGetter: (member: T) => string | null | undefined,
): string => {
    return (
        members.map((m) => fieldGetter(m) ?? '').join(JOINED_DATA_SEPARATOR) ??
        ''
    );
};

const getFullName = <
    T extends { firstName: string | null; lastName: string | null },
>(
    member: T,
) => {
    const firstName = member.firstName?.trim() ?? '';
    const lastName = member.lastName?.trim() ?? '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '[not provided yet]';
};

const flattenSession = (
    session: MembershipSummaryGuidanceSession | undefined,
) => {
    if (session == null) {
        return 'N/A';
    }

    const sessionDate = formatToSimpleDateString(session.startTime, {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
    });

    return [session.type, sessionDate].join(JOINED_DATA_SEPARATOR);
};

const getNextSession = (
    sessions: MembershipSummaryGuidanceSession[],
): MembershipSummaryGuidanceSession | undefined => {
    return sessions.find((gs) => new Date(gs.startTime) > new Date());
};

const getPreviousSession = (
    sessions: MembershipSummaryGuidanceSession[],
): MembershipSummaryGuidanceSession | undefined => {
    return sessions.find((gs) => new Date(gs.startTime) < new Date());
};

export enum MemberJourneyStatus {
    PRE_DISCOVERY_TASKS = 'Pre Discovery Tasks', // New member in process of completing pre-Discovery tasks.
    DISCOVERY_BOOKED = 'Discovery Booked', // Discovery session booked, may or may not have all of pre-Discovery data completed.
    PRE_MONEY_MAP_TASKS = 'Pre Money Map Tasks', // Discovery session completed, waiting on member to complete pre-Money-Map session tasks.
    MONEY_MAP_BOOKED = 'Money Map Booked', // Money Map session booked, may or may not have all of pre-Discovery/pre-Money Map data completed.
    AWAITING_MONEY_MAP = 'Awaiting Money Map', // Money Map session completed, no Active Money Map.
    RECEIVED_MONEY_MAP = 'Received Money Map', // Money Map has been received.
    ACCEPTED_MONEY_MAP = 'Accepted Money Map', // Money Map was accepted.
    MOVED_DIRECT_DEPOSIT = 'Moved Direct Deposit', // Direct Deposit moved to Fruitful.
}

// Utility for determining the current state of the member journey for a membership. Eagerly sets the most progressed status as the current status (even if all tasks from a previous status are incomplete).
const getMemberJourneyStatus = (membership: MembershipSummaryDto): string => {
    const { guidanceSession: sessions, moneyMaps } = membership;

    const nextSession = sessions.find(
        (gs) => new Date(gs.startTime) > new Date(),
    );
    const previousSession = sessions.find(
        (gs) => new Date(gs.startTime) < new Date(),
    );
    const moneyMapStatus = moneyMaps[0]?.status;

    // If either member in a membership has moved direct deposit, mark membership as having moved direct deposit.
    if (
        membership.users.some(
            (user) =>
                user.bankingAdoption?.directDepositStatus ===
                BankingAdoptionTaskStatus.COMPLETE,
        )
    ) {
        return MemberJourneyStatus.MOVED_DIRECT_DEPOSIT;
    }

    /**
     * TODO - Add logic for ACCEPTED_MONEY_MAP once rolled out with member_journey_log
     */

    if (moneyMapStatus === MoneyMapStatus.ACTIVE) {
        return MemberJourneyStatus.RECEIVED_MONEY_MAP;
    }

    if (previousSession?.type === GuidanceSessionType.ROADMAP) {
        return MemberJourneyStatus.AWAITING_MONEY_MAP;
    }

    if (nextSession?.type === GuidanceSessionType.ROADMAP) {
        return MemberJourneyStatus.MONEY_MAP_BOOKED;
    }

    if (
        previousSession?.type === GuidanceSessionType.DISCOVERY &&
        nextSession == null
    ) {
        return MemberJourneyStatus.PRE_MONEY_MAP_TASKS;
    }

    if (nextSession?.type === GuidanceSessionType.DISCOVERY) {
        return MemberJourneyStatus.DISCOVERY_BOOKED;
    }

    if (nextSession == null && previousSession == null) {
        return MemberJourneyStatus.PRE_DISCOVERY_TASKS;
    }

    return 'Unknown';
};
